import { AccountantService, LocalStorageService } from "../../services";
import { EnhancedButtonPrimeReactSeverity, useNavigateToLogout } from "../../common/Common";
import {
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	IEnhancedDropDownItem,
	RHookFormTextFieldWithLabel,
	Spacer,
	SubmitButton,
	SubmitButtonVariant,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { IGetListRequest, IVerifyOtpByMessege } from "../../models";
import React, { useEffect, useRef, useState } from "react";
import {
	setSelectedAccountant,
	setSelectedAccountantName,
	setSelectedClient,
	signInSuccess,
} from "../../store/actions";

import { AccountFormLayout } from "../AccountFormLayout";
import { AuthService } from "../../services/AuthService";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact/EnhancedButtonPrimeReact";
import { Grid } from "@mui/material";
import { INITIAL_SNACKBAR_DATA } from "../../common/constants";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";

const durationOptions: IEnhancedDropDownItem[] = [
	{ label: "N/A", value: "" },
	{ label: "30 days", value: 43200 },
	{ label: "60 days", value: 86400 },
	{ label: "90 days", value: 129600 },
];

type FormValues = {
	otp: string;
	expiresInMinutes: number;
};

// Run effect only once
const useEffectOnce = (effect: () => void | (() => void)) => {
	const effectCalled = useRef(false);
	useEffect(() => {
		if (!effectCalled.current) {
			effectCalled.current = true;
			return effect();
		}
	}, []);
};

const OtpFormComponent = () => {
	const formMethods = useForm<FormValues>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [qrCodeBase64, setQrCodeBase64] = useState<string | null>(null);
	const accessToken = localStorage.getItem("accessToken");
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [expireAfterSelectedValue, setExpireAfterSelectedValue] =
		useState<any>(undefined);
	const navigateToLogout = useNavigateToLogout();

	const [accountantQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "acc_title",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const [clientQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "client_name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const dispatch = useDispatch();

	const onSnackbarClosed = (
		_: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") return;
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const generateQrCode = async () => {
		try {
			const response = await AuthService.GenerateQrCode(
				localStorage.getItem("accessToken")
			);
			if (response?.qrCode && response?.accessToken) {
				setQrCodeBase64(response.qrCode);
				localStorage.setItem("accessToken", response.accessToken);
			}
		} catch {
			setSnackbarData({
				open: true,
				message: "Failed to generate QR code",
				severity: "error",
			});
		}
	};

	useEffectOnce(() => {
		generateQrCode();
	});

	const { isPending, mutate: verifyQrOTP } = useMutation({
		mutationFn: AuthService.verifyQrCode,
		onSuccess: async (res: any) => {
			if (res.status && res.user?.twoFactorAuth) {
				localStorage.setItem("accessToken", res.user.accessToken);
				LocalStorageService.setUser(res);
				setSnackbarData({
					open: true,
					message: res.message,
					severity: "success",
				});
				dispatch(signInSuccess(res));

				if (res.user.isSuperStatus !== 400) {
					try {
						const accountantResponse: any =
							await AccountantService.getAccountants(accountantQueryParams, res.user.isSuperStatus);
						if (accountantResponse?.items?.length === 1) {
							dispatch(setSelectedAccountant(accountantResponse.items[0]));
							dispatch(setSelectedAccountantName(accountantResponse.items[0]));
							try {
								const clientResponse: any =
									await AccountantService.getClientByAccountantId(
										accountantResponse.items[0].id,
										res.user.isSuperStatus,
										clientQueryParams,
										navigateToLogout
									);
								if (clientResponse?.items?.length === 1) {
									dispatch(setSelectedClient(clientResponse.items[0]));
								}
							} catch { }
						} else if (accountantResponse?.items?.length === 0) {
							try {
								const clientResponse: any =
									await AccountantService.getClientByAccountantId(
										"",
										res.user.isSuperStatus,
										clientQueryParams,
										navigateToLogout
									);
								if (clientResponse?.items?.length === 1) {
									dispatch(setSelectedClient(clientResponse.items[0]));
								}
							} catch { }
						}
					} catch { }
				}
			} else {
				setSnackbarData({
					open: true,
					message: res.message,
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onSubmit = (formValues: FormValues) => {

		if (!accessToken) return;

		const expiresAfter = formValues.expiresInMinutes
			? new Date(Date.now() + formValues.expiresInMinutes * 60000)
				.toISOString()
				.split("T")[0]
			: undefined;

		const payload: IVerifyOtpByMessege = {
			accessToken,
			otp: formValues.otp,
			expiresAfter,
		};

		verifyQrOTP(payload);
	};

	const onExpireAfterChangeHandler = (params: IEnhancedDropDownItem) => {
		setExpireAfterSelectedValue(params.value);
	};

	return (
		<>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container spacing={2}>
						{qrCodeBase64 && (
							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} textAlign="left">
								<img
									src={qrCodeBase64}
									alt="2FA QR Code"
									style={{ maxWidth: 250 }}
								/>
							</Grid>
						)}

						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<RHookFormTextFieldWithLabel
								label="Enter code from Google Authenticator"
								id="otp"
								testId="otp-field"
								placeholder="Enter OTP"
							/>
						</Grid>

						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<EnhancedDropDownRHFWithLabel
								label="Disable 2FA prompts on this device for"
								testId="otp-expiry-field"
								selectName="expiresInMinutes"
								items={durationOptions}
								onSelectionChangeHandler={onExpireAfterChangeHandler}
								selectedItem={expireAfterSelectedValue}
							/>
						</Grid>
					</Grid>

					<EnhancedButtonPrimeReact
						label="Verify Code"
						raised
						type="submit"
						style={{ marginLeft: "0rem" }}
						loading={isPending}
						severity={EnhancedButtonPrimeReactSeverity.Success}
					/>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId="otp-form-snackbar"
				open={snackbarData.open}
				autoHide={true}
			/>
		</>
	);
};

export const Generate2FaQrForm = () => {
	return (
		<AccountFormLayout
			title="Scan code with Google Authenticator"
			subtitle="Having trouble with this method?"
			subtitleLinkText="Select another method."
			subtitleLinkNavigateTo="/auth-selection-screen-for-login"
			form={<OtpFormComponent />}
		/>
	);
};
